import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ConsentOnboarding } from "./ConsentOnboarding";

const CONSENT_VERSION = "v1.0";

export const ConsentGuard = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [needsConsent, setNeedsConsent] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkConsent = async () => {
      if (!user) {
        setIsChecking(false);
        setNeedsConsent(false);
        return;
      }

      // Skip consent check for demo user
      if (user.email === 'demo@sme.ng') {
        setIsChecking(false);
        setNeedsConsent(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_consent')
          .select('*')
          .eq('user_id', user.id)
          .eq('consent_version', CONSENT_VERSION)
          .maybeSingle();

        if (error) {
          console.error('Error checking consent:', error);
          // On RLS error, don't block access - assume consent exists
          setNeedsConsent(false);
        } else {
          // If no consent record exists, user needs to provide consent
          setNeedsConsent(!data);
        }
      } catch (error) {
        console.error('Error checking consent:', error);
        // On error, don't block access
        setNeedsConsent(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkConsent();
  }, [user]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (needsConsent) {
    return (
      <ConsentOnboarding 
        onComplete={() => setNeedsConsent(false)} 
      />
    );
  }

  return <>{children}</>;
};
